import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevicesClass } from '@app/core/classes';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { RoutingConfig } from '@app/routing/routing.config';
import { AccountService } from '@app/modules/account-modules/account.service';
import { RolesService } from '@app/core/services';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  getActiveBookingCustomerId,
  getActiveBookingPerformerId,
  hasBookingCustomerAccount,
  hasBookingPerformerAccount
} from '@app/modules/account-modules/account.model';
import {
  UpdateBookingCustomerNotificationSettingsDto,
  UpdateBookingPerformerNotificationsSettingsDto
} from '@app/modules/booking-modules/booking.model';
import { forkJoin } from 'rxjs';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';

export enum MessengerType {
  vk,
  telegram,
}

@UntilDestroy()
@Component({
  selector: 'app-account-discount',
  templateUrl: 'settings.component.html',
  styleUrls: [`./settings.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class SettingsComponent extends DevicesClass implements OnInit {
  public BreadcrumbsData: BreadcrumbsDataModel = [
    {
      title: 'Личный кабинет',
      url: '/' + RoutingConfig.Account
    },
    {
      title: 'Настройки',
      url: '/' + RoutingConfig.AccountDiscount,
      active: true
    }
  ];

  public SettingsForm: FormGroup;
  public BookingSettingsForm: FormGroup | null = null;
  public PerformerBookingSettingsForm: FormGroup | null = null;
  public showBookingCustomerSettings = false;
  public showBookingPerformerSettings = false;
  public bookingCustomerId: string | null = null;
  public bookingPerformerId: string | null = null;
  public SettingsFormModify: boolean;
  public SettingsLoading: boolean;

  /**
   * Messengers settings
   */
  public TelegramNotifyEnabled: boolean;
  public VKNotifyEnabled: boolean;
  public MType = MessengerType;

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private notificationService: NotificationService,
    private rolesService: RolesService,
    private bookingService: BookingService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.SettingsForm = this.formBuilder.group({
      musicianSearch: [false, Validators.required],
      email: [false, Validators.required],
      news: [false, Validators.required]
    });

    const accountProfiles = { profiles: this.rolesService.accountProfiles };
    this.showBookingCustomerSettings = hasBookingCustomerAccount(accountProfiles);
    this.bookingCustomerId = getActiveBookingCustomerId(accountProfiles);
    this.showBookingPerformerSettings = hasBookingPerformerAccount(accountProfiles);
    this.bookingPerformerId = getActiveBookingPerformerId(accountProfiles);

    if (this.showBookingCustomerSettings && this.bookingCustomerId) {
      this.BookingSettingsForm = this.formBuilder.group({
        mainBookingNotificationsEnabled: [true],
        newPerformersNotificationsEnabled: [true],
        performerStateChangesNotificationsEnabled: [true]
      });
      this.loadBookingCustomerNotificationSettings();
      this.BookingSettingsForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.SettingsFormModify = true;
        this.cdr.markForCheck();
      });
    }

    if (this.showBookingPerformerSettings && this.bookingPerformerId) {
      this.PerformerBookingSettingsForm = this.formBuilder.group({
        mainBookingNotificationsEnabled: [true],
        newVenueRegistrationNotificationsEnabled: [true],
        venuesOnlyWithAppliedBookingNotificationsEnabled: [true]
      });
      this.loadPerformerBookingNotificationSettings();
      this.PerformerBookingSettingsForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.SettingsFormModify = true;
        this.cdr.markForCheck();
      });
    }

    this.onSettingsFormCtrl();
    this.onGetSettingsValue();
  }

  private loadBookingCustomerNotificationSettings(): void {
    const id = this.bookingCustomerId;
    if (!id || !this.BookingSettingsForm) {
      return;
    }
    this.bookingService.getBookingCustomerNotifications(id).pipe(untilDestroyed(this)).subscribe(
      data => {
        this.BookingSettingsForm!.patchValue(
          {
            mainBookingNotificationsEnabled: !!data.mainBookingNotificationsEnabled,
            newPerformersNotificationsEnabled: !!data.newPerformersNotificationsEnabled,
            performerStateChangesNotificationsEnabled: !!data.performerStateChangesNotificationsEnabled
          },
          { emitEvent: false }
        );
        this.cdr.markForCheck();
      },
      () => {}
    );
  }

  private loadPerformerBookingNotificationSettings(): void {
    const id = this.bookingPerformerId;
    if (!id || !this.PerformerBookingSettingsForm) {
      return;
    }
    this.bookingService.getPerformerBookingNotifications(id).pipe(untilDestroyed(this)).subscribe(
      data => {
        this.PerformerBookingSettingsForm!.patchValue(
          {
            mainBookingNotificationsEnabled: !!data.mainBookingNotificationsEnabled,
            newVenueRegistrationNotificationsEnabled: !!data.newVenueRegistrationNotificationsEnabled,
            venuesOnlyWithAppliedBookingNotificationsEnabled: !!(
              data.venuesOnlyWithAppliedBookingNotificationsEnabled ?? data.venuesOnlyWithAppliedBooking
            )
          },
          { emitEvent: false }
        );
        this.cdr.markForCheck();
      },
      () => {}
    );
  }

  onGetSettingsValue(): void {
    this.accountService.getSettingsNotification().subscribe(
      response => {
        for (const key of Object.keys(response)) {
          if (this.SettingsForm.controls[key]) {
            this.SettingsForm.controls[key].setValue(
              response[key], { emitEvent: false }
            );
          }
        }

        this.TelegramNotifyEnabled = response.telegramNotifyEnabled;
        this.VKNotifyEnabled = response.vkNotifyEnabled;

        this.cdr.markForCheck();
      }
    )
  }

  onSettingsFormCtrl(): void {
    this.SettingsForm.valueChanges.pipe(untilDestroyed(this)).subscribe(
      value => {
        this.SettingsFormModify = true;
        this.cdr.markForCheck();
      }
    )
  }

  onMessengerGetDeepLinkIdentifier(type: MessengerType): void {
    this.accountService.getDeepLinkIdentifier(type).subscribe(
      response => {
        window.open(
          `${response}`, '_blank'
        );
      }
    )
  }

  onMessengerUnsubscribe(type: MessengerType): void {
    this.accountService.unsubscribeNotification(type).subscribe(
      response => {
        this.onGetSettingsValue();
      }
    )
  }

  onSaveSettings(): void {
    const saves = [
      this.accountService.saveSettingsNotification(this.SettingsForm.getRawValue())
    ];

    if (this.showBookingCustomerSettings && this.bookingCustomerId && this.BookingSettingsForm) {
      saves.push(
        this.bookingService.updateBookingCustomerNotifications(
          this.bookingCustomerId,
          this.BookingSettingsForm.getRawValue() as UpdateBookingCustomerNotificationSettingsDto
        )
      );
    }

    if (this.showBookingPerformerSettings && this.bookingPerformerId && this.PerformerBookingSettingsForm) {
      saves.push(
        this.bookingService.updatePerformerBookingNotifications(
          this.bookingPerformerId,
          this.PerformerBookingSettingsForm.getRawValue() as UpdateBookingPerformerNotificationsSettingsDto
        )
      );
    }

    forkJoin(saves).subscribe(
        () => {
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupSuccessTemplate,
            header: 'Обновление профиля',
            content: 'Настройки Вашего профиля успешно обновлены.',
            icons: 'pi pi-user-edit'
          });
          this.SettingsFormModify = false;
          this.cdr.markForCheck();
        },
        () => {
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка',
            content: 'Не удалось обновить настройки Вашего профиля'
          });
          this.SettingsFormModify = false;
          this.cdr.markForCheck();
        }
      );
  }
}
